import { getScopeRole } from '@/services/current-context';
import { breadcrumb } from '@/services/telemetry';

import { formattedPermissions } from '../components/callback/permissionRoleMap';
import { isAssetOnlyInternalRole, isInternalRole } from './roles';

const resolveActiveRole = (): string | null => {
  let role = getScopeRole();
  // Internal roles are authoritative and must survive the defaultRole fallback
  // below — otherwise an internal user with defaultRole set would be silently
  // re-labelled CUSTOMER_ADMIN. Note this only resolves the role: INTERNAL_ADMIN
  // additionally short-circuits to allow-all in the guards, whereas
  // INTERNAL_POST_PROCESSING deliberately does not and is checked against the
  // permission table like a customer role (DV-2751).
  if (isInternalRole(role)) return role;
  if (localStorage.getItem('defaultRole') === 'true') role = 'CUSTOMER_ADMIN';
  return role;
};

const captureDenial = (route: string, role: string | null, reason: string) => {
  // `captureMessage`'s `extra` is filed as an issue and not surfaced inline in
  // the session replay console, so route/role/reason were effectively invisible.
  // A breadcrumb puts them in the replay console at the denial moment.
  // Deliberately not a `track` event: this runs per-render on a denial, and
  // chatty custom events would burn LogRocket's 2000-properties-per-session cap
  // and silently drop later, more important events. `breadcrumb` never throws,
  // so telemetry cannot break the permission gate.
  breadcrumb('permission-denied', { route, role: role ?? '', reason });
};

export const getPermissionForRoute = (route: string): boolean => {
  if (route === 'Help') return true;

  const role = resolveActiveRole();
  if (role === 'INTERNAL_ADMIN') return true;

  if (!role) {
    captureDenial(route, role, 'no-role');
    return false;
  }

  const rolePermissions = formattedPermissions[role];
  if (!rolePermissions) {
    captureDenial(route, role, 'role-not-mapped');
    return false;
  }
  if (!rolePermissions[route]) {
    captureDenial(route, role, 'route-not-mapped');
    return false;
  }
  const allowed = Object.values(rolePermissions[route]).some((p) => p === true);
  if (!allowed) captureDenial(route, role, 'no-actions-permitted');
  return allowed;
};

export const getPermissionForAction = (
  route: string,
  action: string,
): boolean => {
  const role = resolveActiveRole();
  if (role === 'INTERNAL_ADMIN') return true;

  const rolePermissions = role ? formattedPermissions[role] : undefined;
  if (!rolePermissions || !rolePermissions[route]) return false;
  return rolePermissions[route][`${action} :${route}`] === true;
};

/**
 * The create/import/checkout surfaces `canCreateOnRoute` gates, mirrored by the
 * `CreateGuard` wrapper in `App.tsx`. Kept as a shared union so a typo like
 * 'Orders' is a build error rather than a silently-wrong gate, and so the guard
 * and the predicate cannot drift apart.
 */
export type CreateGuardRoute = 'Properties' | 'Order';

/**
 * Gate for create/import/checkout affordances on non-asset surfaces
 * (New Order, New Property, bulk import, cart, payment).
 *
 * These surfaces have always rendered ungated — `PropertyListPage` accepted
 * `canPost` and never read it — so they are visible to roles the permission
 * table says should not have them (CUSTOMER_FINANCE and CUSTOMER_DEVELOPER are
 * both `POST :Order: false`). Switching them to `getPermissionForAction`
 * outright would fix that, but it would also silently remove a button those
 * two roles use today, which is a product decision and not this change's remit.
 *
 * So this deliberately narrows only for asset-only internal roles and returns
 * the existing permissive answer for everyone else. The table stays the source
 * of truth for the role it applies to.
 *
 * MIGRATION: when product signs off on honouring the table for customer roles,
 * delete the `isAssetOnlyInternalRole` line and this collapses to
 * `getPermissionForAction(route, 'POST')` for all roles.
 */
export const canCreateOnRoute = (route: CreateGuardRoute): boolean => {
  // No INTERNAL_ADMIN short-circuit here: admin isn't asset-only, so the
  // permissive branch below already answers true — one copy of the admin
  // allow-all policy lives in getPermissionForRoute/Action, not three.
  if (!isAssetOnlyInternalRole(resolveActiveRole())) return true;
  return getPermissionForAction(route, 'POST');
};
